"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Pencil, Download } from "lucide-react";
import { STATUS_IDS } from "@/lib/constants/statuses";
import { CUSTOMER_TYPE_IDS } from "@/lib/constants/customer-types";
import { toast } from "sonner";
import { ImportTemplatesDialog } from "@/components/show-details/ImportTemplatesDialog";

interface EmailTemplate {
  id: number;
  trigger_type: string;
  name: string;
  subject: string;
  body: string;
  from_name: string;
  reply_to: string;
  status_id: number;
  created_at: string;
  updated_at: string;
}

interface ShowInfo {
  customer_type_id: number;
}

interface EmailsTabProps {
  venueId: number;
  showId: number | null;  // null for road show templates
}

const formatTriggerType = (triggerType: string): string => {
  return triggerType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .replace("24h", "(24h)")
    .replace("2h", "(2h)")
    .replace("3d", "(3 days)")
    .replace("1d", "(1 day)")
    .replace("7d", "(7 days)")
    .replace("Corporate", "- Corporate")
    .replace("Private", "- Private")
    .replace("Addon", "Add-on")
    .replace("Roadshow", "Road Show");
};

const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

export function EmailsTab({ venueId, showId }: EmailsTabProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [showInfo, setShowInfo] = useState<ShowInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const fetchEmailTemplates = async () => {
      setIsLoading(true);
      try {
        // Use different endpoint for road shows (showId = null)
        const endpoint = showId === null
          ? `/api/venues/${venueId}/road-show/email-templates`
          : `/api/venues/${venueId}/shows/${showId}/email-templates`;

        const response = await fetch(endpoint, {
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error("Failed to fetch email templates");
        }

        const { templates, show } = await response.json();

        // Only update state if the request wasn't aborted
        if (!abortController.signal.aborted) {
          setTemplates(templates);
          setShowInfo(show);
        }
      } catch (error: any) {
        // Ignore abort errors
        if (error.name !== "AbortError") {
          console.error("Error fetching email templates:", error);
          toast.error("Failed to load email templates");
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchEmailTemplates();

    // Cleanup function
    return () => {
      abortController.abort();
    };
  }, [venueId, showId, refreshIndex]);

  return (
    <div className="space-y-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <p className="text-sm text-gray-600">
          Configure automated emails for different booking events
        </p>
        {showId !== null && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsImportOpen(true)}
            className="shrink-0"
          >
            <Download className="h-4 w-4 mr-2" />
            Import from other show
          </Button>
        )}
      </div>

      {showId !== null && (
        <ImportTemplatesDialog
          open={isImportOpen}
          onOpenChange={setIsImportOpen}
          venueId={venueId}
          showId={showId}
          onImported={() => setRefreshIndex((i) => i + 1)}
        />
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-400">Loading...</div>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trigger
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Modified
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates
                .filter((template) => {
                  if (!showInfo) return true; // Show all if no show info

                  // Define which triggers are for which customer types
                  const corporateOnlyTriggers = [
                    "reserved_corporate",
                    "reminder_3d_corporate",
                    "reminder_1d_corporate",
                  ];
                  const privateOnlyTriggers = [
                    "reserved_private",
                    "reminder_3d_private",
                    "reminder_1d_private",
                  ];
                  // Common triggers that show for all customer types: preliminary_reservation, reserved_roadshow,
                  // updated_small_changes, updated_time_change, thanks_for_visiting, reminder_7d, addon_special_questions,
                  // payment_received, reminder_24h, reminder_2h, post_show, booking_cancellation, booking_modification

                  // Show corporate-only templates only for corporate shows
                  if (corporateOnlyTriggers.includes(template.trigger_type)) {
                    return (
                      showInfo.customer_type_id === CUSTOMER_TYPE_IDS.CORPORATE
                    );
                  }

                  // Show private-only templates only for private shows
                  if (privateOnlyTriggers.includes(template.trigger_type)) {
                    return (
                      showInfo.customer_type_id === CUSTOMER_TYPE_IDS.PRIVATE
                    );
                  }

                  // Show all other templates for all show types
                  return true;
                })
                .map((template) => {
                  const isActive =
                    template.status_id === STATUS_IDS.EMAIL_TEMPLATE_ACTIVE;
                  const isDraft =
                    template.status_id === STATUS_IDS.EMAIL_TEMPLATE_DRAFT;

                  return (
                    <tr key={template.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {formatTriggerType(template.trigger_type)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            isActive
                              ? "bg-green-100 text-green-800"
                              : isDraft
                                ? "bg-gray-100 text-gray-800"
                                : "bg-red-100 text-red-800"
                          }`}
                        >
                          {isActive ? "Active" : isDraft ? "Draft" : "Archived"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {template.updated_at
                          ? getRelativeTime(template.updated_at)
                          : `Created ${getRelativeTime(template.created_at)}`
                        }
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            // Determine the current portal from the pathname
                            const portal = pathname.includes("/superadmin/")
                              ? "superadmin"
                              : "booking";
                            // Use different URL for road shows (showId = null)
                            const editUrl = showId === null
                              ? `/${portal}/venue/${venueId}/road-show/emails/${template.id}`
                              : `/${portal}/venue/${venueId}/shows/${showId}/emails/${template.id}`;
                            router.push(editUrl);
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              {templates.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No email templates configured yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-sm mb-2">Legend:</h4>
        <div className="space-y-1 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
              Active
            </span>
            <span>Template is enabled and emails will be sent</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
              Draft
            </span>
            <span>Template is saved but not active</span>
          </div>
        </div>
      </div>
    </div>
  );
}
